import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardMedia,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    TextField,
    Typography,
    CircularProgress
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import './activities.css';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import PALocation from "../../../MasterEventObjects/Location";
import { useGoogleAutocompleteUniversal } from "../../Helpers/GoogleAutocomplete";

function ActivityDialogEditLocation({ isOpen, onClose, eventActivity, setEventActivity }) {

    const { PA_GlobalObject } = usePA_GlobalStore();
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const [activityLocation, setActivityLocation] = useState({});
    const [tabValueLocation, setTabValueLocation] = useState("search");

    // ─── MAP NEAR ME STATES ─────────────────────────────────────────────
    const [isLocating, setIsLocating] = useState(false);
    const [geoError, setGeoError] = useState("");
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerInstanceRef = useRef(null);

    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isOpen) {
            if (isFirstRender.current) {
                setActivityLocation(eventActivity.location || {});
                setTabValueLocation("search");
                isFirstRender.current = false;
            }
        } else {
            isFirstRender.current = true;
        }
    }, [isOpen, eventActivity.location]);

    // Aggregate unique locations from the global event
    useEffect(() => {
        const meals = PA_GlobalObject.Planning.meals || [];
        const lodging = PA_GlobalObject.Planning.lodging || [];
        const activities = PA_GlobalObject.Planning.activities || [];

        const allLocations = [
            ...meals.map((meal) => meal.location),
            ...lodging.map((l) => l.location),
            ...activities.map((a) => a.location),
        ].filter(Boolean);

        const unique = Array.from(
            new Map(
                allLocations
                    .filter(loc => loc.name && loc.google_place_id)
                    .map((loc) => [loc.google_place_id, loc])
            ).values()
        );

        const sortedUnique = unique.sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const matchingLocation = sortedUnique.filter(
            (loc) => activityLocation && loc.google_place_id === activityLocation.google_place_id
        );
        const otherLocations = sortedUnique.filter(
            (loc) => !activityLocation || loc.google_place_id !== activityLocation.google_place_id
        );

        setUniqueLocations([...matchingLocation, ...otherLocations]);
    }, [PA_GlobalObject, activityLocation?.google_place_id]);

    const handleTabLocationChange = (event, newValue) => {
        setTabValueLocation(newValue);
    };

    const handleLocationFieldChange = (field) => (e) => {
        const value = e.target.value;
        setActivityLocation((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    function handleLocationSelect(loc) {
        if (activityLocation?.google_place_id === loc.google_place_id) {
            setActivityLocation({});
        } else {
            setActivityLocation({
                google_place_id: loc.google_place_id || "",
                name: loc.name || "",
                formatted_address: loc.formatted_address || loc.address || "",
                address: loc.address || "",
                city: loc.city || "",
                state: loc.state || "",
                zip: loc.zip || "",
                country: loc.country || "",
                latitude: loc.latitude || 0,
                longitude: loc.longitude || 0,
                phone: loc.phone || "",
                phone_prefix: loc.phone_prefix || "",
                website: loc.website || loc.Place?.website || "",
                hours: loc.hours || [],
                rating: loc.rating || 0,
                total_ratings: loc.total_ratings || 0,
                Place: {
                    website: loc.Place?.website || loc.website || "",
                    photos: loc.Place?.photos || loc.photos || []
                }
            });
        }
    }

    const parseV2Address = (place) => {
        let _address_line = "", _address_city = "", _address_state = "", _address_zip = "", _address_country = "";
        const components = place.addressComponents || place.address_components || [];

        const _country_comp = components.find(c => c.types.includes("country"));
        const _country_code = _country_comp ? (_country_comp.shortText || _country_comp.short_name || "") : "";

        components.forEach((component) => {
            const types = component.types;
            const longVal = component.longText || component.long_name || "";
            const shortVal = component.shortText || component.short_name || "";

            if (types.includes("street_number")) {
                _address_line = longVal;
            } else if (types.includes("route")) {
                _address_line = _address_line ? `${_address_line} ${longVal}` : longVal;
            } else if (types.includes("locality")) {
                _address_city = longVal;
            } else if (types.includes("administrative_area_level_1")) {
                _address_state = (_country_code === "US") ? shortVal : longVal;
            } else if (types.includes("postal_code")) {
                _address_zip = longVal;
            } else if (types.includes("country")) {
                _address_country = shortVal;
            }
        });

        if (!_address_line) {
            const fallbackFormatted = place.formattedAddress || place.formatted_address;
            if (fallbackFormatted) {
                _address_line = fallbackFormatted.split(',')[0].trim();
            }
        }
        return { _address_line, _address_city, _address_state, _address_zip, _address_country };
    };

    const handleUniversalSelect = (place, isBusiness) => {
        if (!place) return;

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        const originalPlace = place;
        const rawPlace = typeof place.toJSON === 'function' ? place.toJSON() : place;

        const parsedAddr = parseV2Address(rawPlace);

        const mapToPALocation = (p, parsed, livePlaceInstance) => {
            const loc = new PALocation();
            loc.Place = livePlaceInstance;

            loc.google_place_id = p.id || p.place_id || "";
            loc.name = p.displayName?.text || p.displayName || p.name || "";

            let latVal = null;
            let lngVal = null;
            if (p.location) {
                latVal = typeof p.location.lat === 'function' ? p.location.lat() : p.location.lat;
                lngVal = typeof p.location.lng === 'function' ? p.location.lng() : p.location.lng;
            } else if (p.geometry?.location) {
                latVal = typeof p.geometry.location.lat === 'function' ? p.geometry.location.lat() : p.geometry.location.lat;
                lngVal = typeof p.geometry.location.lng === 'function' ? p.geometry.location.lng() : p.geometry.location.lng;
            }
            loc.latitude = latVal;
            loc.longitude = lngVal;

            loc.formatted_address = p.formattedAddress || p.formatted_address || "";
            loc.address = parsed._address_line;
            loc.city = parsed._address_city;
            loc.state = parsed._address_state;
            loc.zip = parsed._address_zip;
            loc.country = parsed._address_country;

            loc.phone = p.internationalPhoneNumber || p.international_phone_number || p.formatted_phone_number || "";
            loc.types = p.types || [];
            loc.website = p.websiteURI || p.website || "";
            loc.price_level = p.priceLevel !== undefined ? p.priceLevel : p.price_level;
            loc.rating = p.rating;
            loc.total_ratings = p.userRatingCount || p.user_ratings_total || 0;

            loc.hours = p.regularOpeningHours?.weekdayDescriptions ||
                p.opening_hours?.weekday_text ||
                [];

            return loc;
        };

        setActivityLocation(mapToPALocation(rawPlace, parsedAddr, originalPlace));
    };

    const {
        containerRef: locationRef,
        initAutocomplete: initUniversal,
        autocompleteInstance
    } = useGoogleAutocompleteUniversal(handleUniversalSelect);

    useEffect(() => {
        if (tabValueLocation === "search" && locationRef.current) {
            initUniversal();
        }
    }, [tabValueLocation, locationRef.current]);

    // ─── GEOLOCATION INTERACTIVE MAP EFFECT ─────────────────────────────────
    useEffect(() => {
        if (isOpen && tabValueLocation === 'nearme' && mapContainerRef.current) {
            setGeoError("");
            setIsLocating(true);

            if (!navigator.geolocation) {
                setGeoError("Geolocation is not supported by your current browser device.");
                setIsLocating(false);
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const userLat = position.coords.latitude;
                    const userLng = position.coords.longitude;
                    setIsLocating(false);

                    try {
                        const { Map } = await window.google.maps.importLibrary("maps");
                        const { AdvancedMarkerElement } = await window.google.maps.importLibrary("marker");
                        const { Place } = await window.google.maps.importLibrary("places");

                        const mapOptions = {
                            center: { lat: userLat, lng: userLng },
                            zoom: 16,
                            mapId: "DEMO_MAP_ID",
                            clickableIcons: true,
                            disableDefaultUI: true,
                            zoomControl: true
                        };

                        const map = new Map(mapContainerRef.current, mapOptions);
                        mapInstanceRef.current = map;

                        const marker = new AdvancedMarkerElement({
                            map: map,
                            position: { lat: userLat, lng: userLng },
                            title: "Your Location"
                        });
                        markerInstanceRef.current = marker;

                        map.addListener("click", async (event) => {
                            if (event.placeId) {
                                event.stop();

                                try {
                                    const mapPlace = new Place({ id: event.placeId });

                                    await mapPlace.fetchFields({
                                        fields: [
                                            'id', 'displayName', 'formattedAddress', 'location',
                                            'addressComponents', 'internationalPhoneNumber',
                                            'websiteURI', 'regularOpeningHours', 'photos', 'rating', 'userRatingCount'
                                        ]
                                    });

                                    handleUniversalSelect(mapPlace, true);

                                    if (mapPlace.location) {
                                        marker.position = { lat: mapPlace.location.lat(), lng: mapPlace.location.lng() };
                                    }
                                } catch (err) {
                                    console.error("Error retrieving map point place values: ", err);
                                }
                            }
                        });

                    } catch (error) {
                        console.error("Error launching dynamic map configuration layout:", error);
                        setGeoError("Failed to initialize tracking maps canvas view.");
                    }
                },
                (error) => {
                    setIsLocating(false);
                    if (error.code === error.PERMISSION_DENIED) {
                        setGeoError("Location access authorization denied by user settings.");
                    } else {
                        setGeoError("Unable to safely determine your current location position.");
                    }
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        }
    }, [isOpen, tabValueLocation]);

    function saveEventActivitySettings() {

        const updatedActivity = {
            ...eventActivity,
            location: activityLocation
        };

        setEventActivity(updatedActivity);

        onClose();
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 350,
                    maxWidth: 520,
                    width: '100%',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflow: 'visible !important'
                },
            }}
            sx={{ overflow: 'visible !important', '& .MuiDialog-container': { overflow: 'visible !important' } }}
        >
            <DialogTitle
                sx={{
                    color: '#1e293b',
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    pt: 3,
                    pb: 1
                }}
            >
                Activity Location Setup
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 16, top: 16, color: '#64748b' }}
            >
                <i className="icon-cross" style={{ fontSize: '16px' }} />
            </IconButton>

            <DialogContent>
                <Card
                    sx={{
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.45)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)',
                        overflow: 'visible !important',
                        '& .MuiCardContent-root': { overflow: 'visible !important' },
                        mb: 2
                    }}
                >
                    <CardHeader
                        title="Selected Destination"
                        subheader="Where the activity takes place."
                        sx={{
                            p: '16px 16px 0 16px',
                            "& .MuiCardHeader-title": { fontSize: "0.95rem", fontWeight: 700, color: '#1e293b' },
                            "& .MuiCardHeader-subheader": { fontSize: "12px", color: "text.secondary", mt: 0.25 },
                        }}
                    />
                    <CardContent sx={{ pb: '16px !important', overflow: 'visible !important', pt: 1.5 }}>
                        {activityLocation && (activityLocation.name || activityLocation.address) ? (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    background: 'rgba(240, 150, 55, 0.06)',
                                    border: '1px dashed rgba(240, 150, 55, 0.3)',
                                    borderRadius: '12px',
                                    animation: 'fadeIn 0.3s ease-out'
                                }}
                            >
                                {activityLocation['Place']?.photos && activityLocation['Place'].photos.length > 0 && (
                                    <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        overflowX: 'auto',
                                        WebkitOverflowScrolling: 'touch'
                                    }}>
                                        {activityLocation['Place'].photos.map((_photo, index) => {
                                            let imgSrc = "";
                                            if (typeof _photo.getURI === 'function') {
                                                imgSrc = _photo.getURI({ maxWidth: 150, maxHeight: 150 });
                                            } else if (typeof _photo.getUrl === 'function') {
                                                imgSrc = _photo.getUrl({ maxWidth: 150, maxHeight: 150 });
                                            } else {
                                                imgSrc = _photo.url || _photo.absoluteUrl || "";
                                            }

                                            if (!imgSrc) return null;

                                            return (
                                                <CardMedia
                                                    key={index}
                                                    component="img"
                                                    sx={{
                                                        marginRight: '10px',
                                                        marginBottom: '8px',
                                                        borderRadius: '8px',
                                                        maxHeight: '150px',
                                                        width: 'auto',
                                                        objectFit: 'cover',
                                                        flexShrink: 0
                                                    }}
                                                    alt={`Location Photo ${index + 1}`}
                                                    image={imgSrc}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#2c3e50' }}>
                                    {activityLocation.name || "Unnamed Location"}
                                </Typography>
                                {activityLocation.address && (
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                        {activityLocation.address}
                                    </Typography>
                                )}
                                {(activityLocation.city || activityLocation.state || activityLocation.zip) && (
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                        {activityLocation.city || ''} {activityLocation.state || ''} {activityLocation.zip || ''}
                                    </Typography>
                                )}
                                {activityLocation.phone && (
                                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                        <i className="icon-phone2" style={{ marginRight: '4px' }} />
                                        {activityLocation.phone_prefix || ''} {activityLocation.phone}
                                    </Typography>
                                )}

                                {(activityLocation.website || (Array.isArray(activityLocation.hours) && activityLocation.hours.length > 0)) && (
                                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                                        {activityLocation.website && (
                                            <span onClick={() => window.open(activityLocation.website, '_blank')} style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600, fontSize: '12px', textDecoration: 'underline' }}>
                                                Website
                                            </span>
                                        )}
                                        {Array.isArray(activityLocation.hours) && activityLocation.hours.length > 0 && (
                                            <span onClick={() => alert(activityLocation.hours.map(l => l.replace(/12:00 AM/g, 'midnight').replace(/12:00 PM/g, 'noon')).join('\n'))} style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600, fontSize: '12px', textDecoration: 'underline' }}>
                                                View Hours
                                            </span>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    p: 2.5,
                                    background: 'rgba(0, 0, 0, 0.02)',
                                    border: '1px dashed rgba(0, 0, 0, 0.08)',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.5
                                }}
                            >
                                <i className="icon-map-pin" style={{ fontSize: '20px', color: '#94a3b8' }} />
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>
                                    No location attached
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
                                    Use the search tab or address entry below to add one.
                                </Typography>
                            </Box>
                        )}

                        <TabContext value={tabValueLocation}>
                            <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', mt: 1 }}>
                                <TabList
                                    sx={{ background: 'rgba(255, 255, 255, 0.6)', '& .MuiTabs-indicator': { backgroundColor: '#f09637' } }}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    onChange={handleTabLocationChange}
                                    aria-label="Activity Location Options"
                                >
                                    <Tab label="Search" value="search" sx={{ fontSize: '11px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                                    <Tab label="Near Me" value="nearme" sx={{ fontSize: '11px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                                    <Tab label="Locations" value="locations" sx={{ fontSize: '11px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                                    <Tab label="Address" value="address" sx={{ fontSize: '11px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                                </TabList>
                            </Box>

                            <TabPanel
                                value="search"
                                sx={{
                                    p: '16px 0 0 0',
                                    position: 'relative',
                                    pb: '140px !important',
                                    overflow: 'visible !important'
                                }}
                            >
                                <Box
                                    ref={locationRef}
                                    onTouchStart={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation();
                                            if (document.activeElement instanceof HTMLElement) {
                                                document.activeElement.blur();
                                            }
                                        }
                                    }}
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    sx={{
                                        flex: 1,
                                        minHeight: '40px',
                                        width: '100%',
                                        overflow: 'visible !important',
                                        position: 'relative',
                                        zIndex: 1100,
                                        '& gmp-place-autocomplete': {
                                            width: '100%',
                                            display: 'block',
                                            colorScheme: 'light !important',
                                            backgroundColor: '#ffffff !important',
                                            borderRadius: '12px !important',
                                        },
                                        '& .pac-container, & ~ .pac-container': {
                                            zIndex: '3000 !important',
                                            visibility: 'visible !important',
                                            display: 'block !important',
                                            boxShadow: '0 8px 16px rgba(0,0,0,0.1) !important',
                                            border: '1px solid rgba(0,0,0,0.06) !important',
                                            position: 'absolute !important',
                                            top: '100% !important',
                                            left: '0 !important',
                                            width: '100% !important'
                                        }
                                    }}
                                />
                            </TabPanel>

                            <TabPanel value="nearme" sx={{ p: '16px 0 0 0' }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '240px',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '1px solid rgba(0,0,0,0.1)',
                                        position: 'relative',
                                        background: '#f8fafc',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {isLocating && (
                                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                            <CircularProgress size={28} sx={{ color: '#f09637' }} />
                                            <Typography sx={{ fontSize: '11px', color: '#64748b' }}>Awaiting device location coordinates...</Typography>
                                        </Box>
                                    )}
                                    {geoError && (
                                        <Typography sx={{ fontSize: '12px', color: '#ef4444', p: 2, textAlign: 'center', fontWeight: 500 }}>
                                            {geoError}
                                        </Typography>
                                    )}
                                    <div
                                        ref={mapContainerRef}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            display: (isLocating || geoError) ? 'none' : 'block'
                                        }}
                                    />
                                </Box>
                                {!isLocating && !geoError && (
                                    <Typography sx={{ fontSize: '11px', color: '#64748b', textAlign: 'center', mt: 1 }}>
                                        💡 Tap on any business pin or icon on the map canvas to choose it.
                                    </Typography>
                                )}
                            </TabPanel>

                            <TabPanel value="locations" sx={{ p: '16px 0 0 0' }}>
                                <Box sx={{ flex: '1', maxHeight: '220px', overflowY: 'auto' }}>
                                    <List dense>
                                        {uniqueLocations.length > 0 ? (
                                            uniqueLocations.map((location, index) => (
                                                <ListItem key={index} sx={{ alignItems: "flex-start", borderRadius: '8px', mb: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' } }} button onClick={() => handleLocationSelect(location)}>
                                                    <Checkbox
                                                        checked={activityLocation?.google_place_id === location.google_place_id}
                                                        onChange={() => handleLocationSelect(location)}
                                                        sx={{ padding: "0 8px 0 0", '&.Mui-checked': { color: '#f09637' } }}
                                                    />
                                                    <ListItemText
                                                        primary={location.name || "Unnamed Location"}
                                                        secondary={location.address || "No Address Available"}
                                                        primaryTypographyProps={{ fontWeight: 600, fontSize: '13px' }}
                                                        secondaryTypographyProps={{ fontSize: '11px' }}
                                                    />
                                                </ListItem>
                                            ))
                                        ) : (
                                            <Typography variant="body2" sx={{ textAlign: "center", color: "gray", py: 2 }}>
                                                No Event locations to choose from yet
                                            </Typography>
                                        )}
                                    </List>
                                </Box>
                            </TabPanel>

                            <TabPanel value="address" sx={{ p: '16px 0 0 0', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {["name", "address", "city", "state", "zip", "country"].map((field) => (
                                    <TextField
                                        key={field}
                                        label={field.charAt(0).toUpperCase() + field.slice(1).replace('zip', 'Zip Code')}
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={activityLocation?.[field] || ""}
                                        onChange={handleLocationFieldChange(field)}
                                        InputProps={{ sx: { borderRadius: '10px', background: 'rgba(255,255,255,0.4)' } }}
                                    />
                                ))}
                            </TabPanel>
                        </TabContext>
                    </CardContent>
                </Card>
            </DialogContent>

            <DialogActions sx={{ p: '0 24px 24px 24px' }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600 }}>Close</Button>
                <Button
                    onClick={saveEventActivitySettings}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #f09637 0%, #f2a654 100%)',
                        boxShadow: '0 4px 14px rgba(240, 150, 55, 0.3)',
                        borderRadius: '10px',
                        fontWeight: 700,
                        px: 3,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #e08526 0%, #f09637 100%)',
                        }
                    }}
                >
                    Save Location
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ActivityDialogEditLocation;